import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { PdfEmptyText, PdfTable } from '@components/PdfExport/PdfExport.styles';
import PdfSection from '@components/PdfExport/PdfSection';

import type {
  PdfMedicationData,
  PdfMedicationEntry,
} from '@hooks/useMedicationPdfData';

import { formatDate } from '@utils/format';

import {
  AvatarCircle,
  GeneratedLabel,
  HeaderLeft,
  HeaderRight,
  HeaderRow,
  PageFooter,
  PatientName,
} from '@views/HealthSummary/Pdf/HealthSummaryPdfTemplate.style';

import {
  MedicationName,
  MedicationSubtype,
  NotesLine,
} from './MedicationPdfTemplate.style';

const messages = defineMessages({
  generatedOn: {
    id: 'medicationsPdf.generatedOn',
    defaultMessage: 'Generated',
  },
  activeMedications: {
    id: 'medicationsPdf.activeMedications',
    defaultMessage: 'Active Medications',
  },
  pastMedications: {
    id: 'medicationsPdf.pastMedications',
    defaultMessage: 'Past Medications',
  },
  noActiveMedications: {
    id: 'medicationsPdf.noActiveMedications',
    defaultMessage: 'No active medications listed',
  },
  noPastMedications: {
    id: 'medicationsPdf.noPastMedications',
    defaultMessage: 'No past medications listed',
  },
  medication: {
    id: 'medicationsPdf.medication',
    defaultMessage: 'Medication',
  },
  status: {
    id: 'medicationsPdf.status',
    defaultMessage: 'Status',
  },
  dosage: {
    id: 'medicationsPdf.dosage',
    defaultMessage: 'Dosage',
  },
  route: {
    id: 'medicationsPdf.route',
    defaultMessage: 'Route',
  },
  dates: {
    id: 'medicationsPdf.dates',
    defaultMessage: 'Start – End',
  },
  instructions: {
    id: 'medicationsPdf.instructions',
    defaultMessage: 'Instructions',
  },
  unknown: {
    id: 'GLOBAL.unknown',
    defaultMessage: 'Unknown',
  },
  confidentiality: {
    id: 'healthSummaryPdf.confidentiality',
    defaultMessage:
      'Confidential health record — handle in accordance with applicable privacy regulations',
  },
});

interface MedicationPdfTemplateProps {
  data: PdfMedicationData;
}

function formatDateRange(
  startDate: string | null,
  endDate: string | null
): string {
  if (startDate && endDate) {
    return `${startDate} – ${endDate}`;
  }
  if (startDate) {
    return `${startDate} – `;
  }
  if (endDate) {
    return endDate;
  }
  return '—';
}

export default function MedicationPdfTemplate({
  data,
}: MedicationPdfTemplateProps) {
  const { formatMessage } = useIntl();
  const today = formatDate({ date: new Date(), formatter: 'date' });

  const renderTable = (entries: PdfMedicationEntry[], emptyMessage: string) => {
    if (entries.length === 0) {
      return <PdfEmptyText>{emptyMessage}</PdfEmptyText>;
    }
    return (
      <PdfTable>
        <thead>
          <tr>
            <th>{formatMessage(messages.medication)}</th>
            <th>{formatMessage(messages.status)}</th>
            <th>{formatMessage(messages.dosage)}</th>
            <th>{formatMessage(messages.route)}</th>
            <th>{formatMessage(messages.dates)}</th>
            <th>{formatMessage(messages.instructions)}</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => (
            <tr key={entry.id}>
              <td>
                <MedicationName>
                  {entry.name || formatMessage(messages.unknown)}
                </MedicationName>
                {entry.acquisitionTypeName && (
                  <MedicationSubtype>
                    {entry.acquisitionTypeName}
                  </MedicationSubtype>
                )}
              </td>
              <td>{entry.status || '—'}</td>
              <td>{entry.dosage || '—'}</td>
              <td>{entry.route || '—'}</td>
              <td>{formatDateRange(entry.startDate, entry.endDate)}</td>
              <td>
                {entry.instructions || '—'}
                {entry.notes && <NotesLine>{entry.notes}</NotesLine>}
              </td>
            </tr>
          ))}
        </tbody>
      </PdfTable>
    );
  };

  return (
    <>
      {/* ── Patient header ── */}
      <HeaderRow>
        <HeaderLeft>
          <PatientName>
            {data.patientName ?? formatMessage(messages.unknown)}
          </PatientName>
        </HeaderLeft>

        <HeaderRight>
          <AvatarCircle>{data.initials}</AvatarCircle>
          <GeneratedLabel>
            {formatMessage(messages.generatedOn)}
            <br />
            {today}
          </GeneratedLabel>
        </HeaderRight>
      </HeaderRow>

      {/* ── Active Medications ── */}
      <PdfSection label={formatMessage(messages.activeMedications)}>
        {renderTable(data.active, formatMessage(messages.noActiveMedications))}
      </PdfSection>

      {/* ── Past Medications ── */}
      <PdfSection label={formatMessage(messages.pastMedications)}>
        {renderTable(data.past, formatMessage(messages.noPastMedications))}
      </PdfSection>

      {/* ── Footer ── */}
      <PageFooter>{formatMessage(messages.confidentiality)}</PageFooter>
    </>
  );
}
